import React, { memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Skeleton,
  Alert,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  AccountBalance,
  ShowChart,
  Hotel,
  CalendarToday
} from '@mui/icons-material';
import { MetricsCardsProps, FinancialMetrics } from '../../types';
import { SyncStatus } from './DataSyncStatus';

interface MetricCardData {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
    label: string;
  };
  color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  format: 'currency' | 'percentage' | 'number';
}

interface ExtendedMetricsCardsProps extends MetricsCardsProps {
  lastUpdated?: string;
  syncStatus?: SyncStatus;
}

const MetricsCards: React.FC<ExtendedMetricsCardsProps> = memo(({
  metrics,
  previousPeriodMetrics,
  lastUpdated,
  syncStatus,
  loading = false,
  error
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Format currency values
  const formatCurrency = (amount: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format percentage values
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // Format number values
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Calculate trend for a metric
  const calculateTrend = (current: number, previous?: number) => {
    if (!previous || previous === 0) return null;
    
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0,
      label: change >= 0 ? 'increase' : 'decrease'
    };
  };

  // Format value based on type
  const formatValue = (value: number, format: 'currency' | 'percentage' | 'number', currency?: string): string => {
    switch (format) {
      case 'currency':
        return formatCurrency(value, currency);
      case 'percentage':
        return formatPercentage(value);
      case 'number':
        return formatNumber(value);
      default:
        return value.toString();
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // Define metric cards data
  const getMetricCards = (): MetricCardData[] => {
    if (!metrics) return [];

    return [
      {
        title: 'Total Revenue',
        value: metrics.totalRevenue,
        icon: <AttachMoney />,
        trend: calculateTrend(metrics.totalRevenue, previousPeriodMetrics?.totalRevenue) || undefined,
        color: 'success',
        format: 'currency'
      },
      {
        title: 'Total Expenses',
        value: metrics.totalExpenses,
        icon: <AccountBalance />,
        trend: calculateTrend(metrics.totalExpenses, previousPeriodMetrics?.totalExpenses) || undefined,
        color: 'error',
        format: 'currency'
      },
      {
        title: 'Net Profit',
        value: metrics.netProfit,
        icon: <ShowChart />,
        trend: calculateTrend(metrics.netProfit, previousPeriodMetrics?.netProfit) || undefined,
        color: metrics.netProfit >= 0 ? 'success' : 'error',
        format: 'currency'
      },
      {
        title: 'Profit Margin',
        value: metrics.profitMargin,
        icon: <TrendingUp />,
        trend: calculateTrend(metrics.profitMargin, previousPeriodMetrics?.profitMargin) || undefined,
        color: metrics.profitMargin >= 0 ? 'success' : 'error',
        format: 'percentage'
      },
      {
        title: 'Avg. Nightly Rate',
        value: metrics.averageNightlyRate,
        icon: <Hotel />,
        trend: calculateTrend(metrics.averageNightlyRate, previousPeriodMetrics?.averageNightlyRate) || undefined,
        color: 'primary',
        format: 'currency'
      },
      {
        title: 'Occupancy Rate',
        value: metrics.occupancyRate,
        icon: <CalendarToday />,
        trend: calculateTrend(metrics.occupancyRate, previousPeriodMetrics?.occupancyRate) || undefined,
        color: 'info',
        format: 'percentage'
      }
    ];
  };

  const metricCards = getMetricCards();

  // Render trend chip
  const renderTrendChip = (trend?: { value: number; isPositive: boolean; label: string }) => {
    if (!trend) return null;

    return (
      <Chip
        icon={trend.isPositive ? <TrendingUp /> : <TrendingDown />}
        label={`${trend.value.toFixed(1)}% ${trend.label}`}
        size="small"
        color={trend.isPositive ? 'success' : 'error'}
        variant="outlined"
        sx={{ 
          fontSize: '0.75rem',
          height: 24,
          '& .MuiChip-icon': {
            fontSize: '0.875rem'
          }
        }}
      />
    );
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <Grid container spacing={isMobile ? 2 : 3}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Box>
              <Skeleton variant="text" width="80%" height={32} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" width="50%" height={24} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (loading) {
    return renderSkeleton();
  }

  // Format last updated timestamp
  const formatLastUpdated = (timestamp?: string): string => {
    if (!timestamp) return 'Unknown';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Box>
      {/* Last Updated Info */}
      {(lastUpdated || syncStatus) && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Data last updated: {formatLastUpdated(lastUpdated)}
            </Typography>
          )}
          
          {syncStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {syncStatus.dataStale && (
                <Chip
                  label="Data may be outdated"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
              {!syncStatus.isOnline && (
                <Chip
                  label="Offline"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {metricCards.map((card, index) => (
        <Grid 
          item 
          xs={12} 
          sm={6} 
          md={4} 
          lg={2} 
          key={index}
        >
          <Card 
            sx={{ 
              height: '100%',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              {/* Header with icon and title */}
              <Box 
                display="flex" 
                alignItems="center" 
                mb={2}
                sx={{ 
                  color: theme.palette[card.color].main 
                }}
              >
                <Box 
                  sx={{ 
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {card.icon}
                </Box>
                <Typography 
                  variant={isMobile ? "body2" : "subtitle2"} 
                  color="text.secondary"
                  sx={{ 
                    fontWeight: 500,
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}
                >
                  {card.title}
                </Typography>
              </Box>

              {/* Main value */}
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                component="div"
                sx={{ 
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette[card.color].main,
                  fontSize: isMobile ? '1.25rem' : '1.5rem'
                }}
              >
                {formatValue(card.value as number, card.format, metrics?.currency)}
              </Typography>

              {/* Trend indicator */}
              <Box 
                display="flex" 
                justifyContent="flex-start"
                alignItems="center"
                minHeight={24}
              >
                {card.trend ? (
                  renderTrendChip(card.trend)
                ) : (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    No comparison data
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
      </Grid>
    </Box>
  );
});

export { MetricsCards };
export default MetricsCards;